import type { ComponentType } from 'react';
import { useState } from 'react';

import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import { BpkSingleSelectChipGroup } from '@skyscanner-internal/backpack-web/bpk-component-chip-group';
import BpkIconFlightLanding from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-landing';
import BpkIconFlightTakeoff from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-takeoff';
import BpkIconAircon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/aircon';
import BpkIconBus from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/bus';
import BpkIconFamily from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/family';
import BpkIconFood from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/food';
import BpkIconHotels from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels';
import BpkIconHotelsDisabledFacilities from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels--disabled-facilities';
import BpkIconHotelsJacuzzi from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels--jacuzzi';
import BpkIconHotelsPetsAllowed from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels--pets-allowed';
import BpkIconHotelsSwimmingPool from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels--swimming-pool';
import BpkIconParking from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/parking';
import BpkIconTick from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/tick';
import BpkIconWifi from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/wifi';
import BpkImage from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkRating, {
  RATING_SIZES,
  RATING_SCALES,
} from '@skyscanner-internal/backpack-web/bpk-component-rating';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  CityTab,
  PackageBadgeType,
  PackageCardProps,
  PackageSearchResultsProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/PackageSearchResultsProps';

import STYLES from './PackageSearchResults.module.scss';

const MAX_BADGES = 8;

// Maps each badge type to its Backpack icon component.
// Add a new entry here (plus the corresponding import) when swapping an icon.
const BADGE_ICON_COMPONENTS: Partial<
  Record<PackageBadgeType, ComponentType<{ className?: string }>>
> = {
  abta: BpkIconTick,
  atol: BpkIconTick,
  meal_plan: BpkIconHotels,
  pet_friendly: BpkIconHotelsPetsAllowed,
  family_friendly: BpkIconFamily,
  air_conditioning: BpkIconAircon,
  pool: BpkIconHotelsSwimmingPool,
  shuttle_service: BpkIconBus,
  parking: BpkIconParking,
  free_wifi: BpkIconWifi,
  accessible: BpkIconHotelsDisabledFacilities,
  restaurant: BpkIconFood,
  spa: BpkIconHotelsJacuzzi,
};

const PackageCard = ({
  buildMBDParam,
  card,
  index,
  trackingComponentName,
}: {
  card: PackageCardProps;
  index: number;
  trackingComponentName: string;
  buildMBDParam: (
    componentName: string,
    params?: Record<string, string>,
  ) => string;
}) => (
  <BpkCard
    atomic={false}
    padded={false}
    className={STYLES.PackageCard}
    data-testid={`package-card-${index}`}
    data-tracking-element-id={`${trackingComponentName}_item_${index}`}
    data-tracking-common-params={buildMBDParam(
      `${trackingComponentName}_item`,
      { hotel_name: card.hotelName },
    )}
  >
    <a
      href={card.deepLink}
      target="_blank"
      rel="nofollow noreferrer"
      aria-label={card.hotelName}
      className={STYLES.PackageCard__link}
    />
    <div
      className={STYLES.PackageCard__image}
      data-tracking-element-id={`${trackingComponentName}_image_${index}`}
      data-tracking-common-params={buildMBDParam(
        `${trackingComponentName}_image`,
        { hotel_name: card.hotelName },
      )}
    >
      <BpkImage
        altText={card.hotelName}
        src={card.imageUrl}
        aspectRatio={1}
        width={268}
        height={268}
      />
    </div>

    <div className={STYLES.PackageCard__content}>
      <div className={STYLES.PackageCard__contentArea}>
        <div className={STYLES.PackageCard__header}>
          <BpkText tagName="h3" textStyle="heading-4">
            {card.hotelName}
          </BpkText>
          {card.subtitle && (
            <BpkText tagName="p" textStyle="footnote">
              {card.subtitle}
            </BpkText>
          )}
        </div>

        {card.rating && card.rating.score > 0 && (
          <BpkRating
            ariaLabel={`${card.rating.scoreDescription} ${card.rating.score} out of 5. ${card.rating.subtitle}`}
            value={card.rating.score}
            title={
              card.rating.scoreImageUrl ? (
                <img
                  src={card.rating.scoreImageUrl}
                  className={STYLES.PackageCard__ratingImage}
                  alt=""
                />
              ) : (
                card.rating.scoreDescription
              )
            }
            subtitle={card.rating.subtitle}
            showScale={!card.rating.scoreImageUrl}
            ratingScale={RATING_SCALES.zeroToFive}
            size={RATING_SIZES.base}
          />
        )}
      </div>

      <div className={STYLES.PackageCard__badges}>
        {/* All badge types with pill border — hidden on mobile via SCSS */}
        {card.badges.slice(0, MAX_BADGES).map((badge) => {
          const BadgeIcon = BADGE_ICON_COMPONENTS[badge.type];
          return (
            <span key={badge.text} className={STYLES.PackageCard__badge}>
              {BadgeIcon && (
                <BadgeIcon className={STYLES.PackageCard__badgeIcon} />
              )}
              {badge.text}
            </span>
          );
        })}
      </div>

      {card.flightInfo && (
        <div className={STYLES.PackageCard__flightDetails}>
          <div className={STYLES.PackageCard__flightLeg}>
            <BpkIconFlightTakeoff
              className={STYLES.PackageCard__flightLegIcon}
            />
            <div>
              <BpkText
                tagName="p"
                textStyle="label-1"
                className={STYLES.PackageCard__flightDate}
              >
                {card.flightInfo.outbound.date}
              </BpkText>
              <BpkText
                tagName="p"
                textStyle="caption"
                className={STYLES.PackageCard__flightRoute}
              >
                {card.flightInfo.outbound.origin} –{' '}
                {card.flightInfo.outbound.destination},{' '}
                {card.flightInfo.outbound.stopsLabel}
              </BpkText>
            </div>
          </div>
          {card.flightInfo.inbound && (
            <div className={STYLES.PackageCard__flightLeg}>
              <BpkIconFlightLanding
                className={STYLES.PackageCard__flightLegIcon}
              />
              <div>
                <BpkText
                  tagName="p"
                  textStyle="label-1"
                  className={STYLES.PackageCard__flightDate}
                >
                  {card.flightInfo.inbound.date}
                </BpkText>
                <BpkText
                  tagName="p"
                  textStyle="caption"
                  className={STYLES.PackageCard__flightRoute}
                >
                  {card.flightInfo.inbound.origin} –{' '}
                  {card.flightInfo.inbound.destination},{' '}
                  {card.flightInfo.inbound.stopsLabel}
                </BpkText>
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    <div className={STYLES.PackageCard__priceArea}>
      <div className={STYLES.PackageCard__priceInfo}>
        {card.packagePriceLabel && (
          <BpkText
            tagName="p"
            textStyle="footnote"
            className={STYLES.PackageCard__holidayType}
          >
            {card.packagePriceLabel}
          </BpkText>
        )}
        <BpkPrice
          price={card.perPersonPrice}
          size={SIZES.large}
          align={ALIGNS.left}
          trailingText={card.perPersonSuffix}
        />
        <BpkText
          tagName="p"
          textStyle="footnote"
          className={STYLES.PackageCard__totalPrice}
        >
          {card.totalPriceLabel}
        </BpkText>
      </div>

      {card.providerLogoUrl && (
        <div className={STYLES.PackageCard__provider}>
          <img
            src={card.providerLogoUrl}
            alt={card.providerName}
            width={120}
            height={60}
            className={STYLES.PackageCard__providerLogo}
          />
        </div>
      )}

      <BpkButton
        href={card.deepLink}
        target="_blank"
        rel="nofollow"
        size="small"
        className={STYLES.PackageCard__cta}
        data-tracking-element-id={`${trackingComponentName}_cta_${index}`}
        data-tracking-common-params={buildMBDParam(
          `${trackingComponentName}_cta`,
        )}
      >
        {card.ctaText}
      </BpkButton>
    </div>
  </BpkCard>
);

const PackageSearchResults = ({
  cityTabs,
  filterLabel,
  sectionDescription,
  sectionTitle,
}: PackageSearchResultsProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!cityTabs || cityTabs.length === 0) {
    return null;
  }

  const selectedTab: CityTab = cityTabs[selectedTabIndex] || cityTabs[0];

  const chips = cityTabs.map((tab) => ({
    text: tab.cityName,
  }));

  return (
    <div
      className={STYLES.PackageSearchResults}
      data-testid="package-search-results"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <BpkSectionHeader title={sectionTitle} description={sectionDescription} />

      {cityTabs.length > 1 && (
        <div className={STYLES.PackageSearchResults__tabs}>
          <BpkText
            tagName="span"
            textStyle="footnote"
            className={STYLES.PackageSearchResults__filterLabel}
          >
            {filterLabel}
          </BpkText>
          <BpkSingleSelectChipGroup
            chips={chips}
            selectedIndex={selectedTabIndex}
            onItemClick={(_item, _selected, index) =>
              setSelectedTabIndex(index)
            }
            type="wrap"
            ariaLabel="Filter by city"
          />
        </div>
      )}

      <div key={selectedTabIndex} className={STYLES.PackageSearchResults__list}>
        {selectedTab.packages.map((pkg, index) => (
          <PackageCard
            key={pkg.id}
            card={pkg}
            index={index}
            trackingComponentName={rawComponentName}
            buildMBDParam={buildMBDParam}
          />
        ))}
      </div>
    </div>
  );
};

export default PackageSearchResults;
